import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon, PrimaryButton, GhostButton } from "@/components/kit";
import { ContactActions } from "@/components/ContactActions";
import { ProspectHeader, ProFooter } from "@/components/prospect";
import { useProBranding } from "@/hooks/useProBranding";
import { api, fileUrl, fileUrlAuth, getToken } from "@/api";
import { useI18n } from "@/i18n";

const eur = (n) => Math.round(n).toLocaleString("fr-FR");

export default function Summary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { pro } = useProBranding();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setQuote(await api.getQuote(id));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const yesno = (b) => (b ? t("yes") : t("no"));
  const liningLabel = (v) => ({ blackout: t("opt_blackout"), thermal: t("opt_thermal"), both: t("opt_both") }[v] || v);

  return (
    <div className="min-h-screen bg-paper">
      <ProspectHeader pro={pro} />
      <div className="mx-auto max-w-3xl px-5 py-8">
        <h1 className="mb-6 font-display text-4xl font-semibold text-ink">{t("sum_title")}</h1>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <Icon name="loader" size={40} className="animate-spin text-brand" />
            <p className="text-[14px] text-mute">{t("sum_loading")}</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <Icon name="alert-circle" size={40} className="text-err" />
            <p className="text-[14px] text-mute">{error}</p>
            <button data-testid="summary-retry" onClick={load} className="text-[15px] font-semibold text-brand">{t("sum_retry")}</button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Estimate hero */}
            <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-brand to-[#8A4C32] p-6 text-white">
              <p className="mb-2 text-[11px] font-semibold tracking-[0.15em] text-white/80">
                {t("est_kicker")} · {t(`cat_${quote.category}`)}
              </p>
              {quote.estimate_min != null ? (
                <p data-testid="estimate-range" className="font-display text-5xl font-bold">
                  {eur(quote.estimate_min)} € <span className="font-normal text-white/70">–</span> {eur(quote.estimate_max)} €
                </p>
              ) : (
                <p className="font-display text-5xl font-bold">{t("on_quote")}</p>
              )}
              {quote.estimate_summary ? <p className="mt-3 text-[13px] leading-relaxed text-white/90">{quote.estimate_summary}</p> : null}
              {["wallpaper", "curtains", "flooring"].includes(quote.category) ? (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-black/20 px-3 py-2.5">
                  <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0 text-white" />
                  <span className="text-[12px] font-semibold leading-snug">{t(`excl_${quote.category}`)}</span>
                </div>
              ) : null}
            </div>

            <PartnersSection quote={quote} />

            {quote.estimate_breakdown?.length > 0 && (
              <Card title={t("breakdown_title")}>
                {quote.estimate_breakdown.map((b, i) => (
                  <DRow key={i} label={b.label} value={b.amount != null ? `${eur(b.amount)} €` : "-"} />
                ))}
              </Card>
            )}

            <Card title={t("recap_title")}>
              <DRow label={t("r_client")} value={quote.client_name} />
              <DRow label={t("r_property")} value={quote.property_type === "house" ? t("opt_house") : t("opt_apartment")} />
              <DRow label={t("r_level")} value={quote.floor_level === "ground" ? t("opt_ground") : t("opt_upper")} />
              {(quote.rooms || []).map((r, i) => {
                const nm = r.name || `${t("room_label")} ${i + 1}`;
                const title = r.floor ? `${nm} — ${r.floor}` : nm;
                const wins = (r.windows || []).filter((w) => w.width || w.height);
                if (quote.category === "curtains") {
                  return (
                    <div key={i}>
                      <DRow label={title} value={`${wins.length} 🪟`} />
                      {wins.map((w, wi) => {
                        const parts = [];
                        if (w.want_voilage) parts.push(t("opt_voilage"));
                        if (w.want_tenture) parts.push(t("opt_tenture"));
                        const drop = w.total_height || w.height || "?";
                        const suffix = parts.length ? ` · ${parts.join("/")}` : "";
                        return <DRow key={wi} label={`  ${t("window_n")} ${wi + 1}`} value={`${w.width || "?"} × ${drop} m${suffix}`} />;
                      })}
                    </div>
                  );
                }
                const dims = quote.category === "flooring"
                  ? (r.floor_area ? `${r.floor_area} m²` : `${r.wall_length || "?"}×${r.wall_width || "?"} m`)
                  : `${r.wall_height || "?"}×${r.wall_length || "?"}×${r.wall_width || "?"} m`;
                const extra = quote.category === "flooring" ? "" : ` · ${r.num_doors || 0} 🚪 · ${wins.length} 🪟`;
                return <DRow key={i} label={title} value={`${dims}${extra}`} />;
              })}
              {quote.paint_location ? <DRow label={t("r_paint")} value={quote.paint_location === "interior" ? t("opt_interior") : t("opt_exterior")} /> : null}
              {quote.category === "curtains" && (
                <>
                  <DRow label={t("r_lining")} value={yesno(quote.needs_lining)} />
                  {quote.needs_lining && quote.lining_type ? <DRow label={t("r_lining_type")} value={liningLabel(quote.lining_type)} /> : null}
                </>
              )}
              {quote.category === "flooring" && (
                <>
                  <DRow label={t("r_radiators")} value={`${quote.num_radiators}`} />
                  <DRow label={t("r_removal")} value={yesno(quote.needs_floor_removal)} />
                  <DRow label={t("r_leveling")} value={yesno(quote.needs_floor_leveling)} />
                  <DRow label={t("r_furniture")} value={yesno(quote.furniture_to_move)} />
                </>
              )}
              {quote.category !== "flooring" && quote.category !== "curtains" && (
                <>
                  <DRow label={t("r_dewallpaper")} value={yesno(quote.needs_dewallpapering)} />
                  <DRow label={t("r_smoothing")} value={yesno(quote.needs_smoothing)} />
                  <DRow label={t("r_repairs")} value={yesno(quote.needs_repairs)} />
                </>
              )}
            </Card>

            {(quote.supply_cost != null || quote.supply_detail || quote.ttc_min != null) && (
              <Card title={quote.supply_cost == null && quote.supply_detail ? t("r_supply_qty") : null}>
                {(() => {
                  const POSE_GROUPS = ["floor", "pose"];
                  const isPose = (it) => it.key !== "floor_covering" && POSE_GROUPS.includes(it.group);
                  const its = quote.supply_items || [];
                  const poseItems = its.filter(isPose);
                  const supplyItems = its.filter((it) => !isPose(it));
                  const supplyCost = its.length ? supplyItems.reduce((s, it) => s + (it.total || 0), 0) : quote.supply_cost;
                  const renderLine = (it, idx) => (
                    <div key={idx} className="flex justify-between gap-3 text-[13px]">
                      <span className="text-ink">{it.label}</span>
                      <span className="text-right font-medium text-mute">
                        {it.unit_price ? `${it.qty} ${it.unit} × ${it.unit_price} € = ${Math.round(it.total)} €` : `${it.qty} ${it.unit}`}
                      </span>
                    </div>
                  );
                  if (quote.supply_cost == null) {
                    return quote.supply_detail ? <p className="mt-1 text-[12px] text-mute">{quote.supply_detail}</p> : null;
                  }
                  return (
                    <>
                      {poseItems.length > 0 ? (
                        <div className="mb-2">
                          <p className="mb-1 text-[13px] font-semibold text-brand">{t("r_pose_acc")}</p>
                          <div className="space-y-1.5">{poseItems.map(renderLine)}</div>
                        </div>
                      ) : null}
                      <DRow label={t("r_supply")} value={`${eur(supplyCost)} €`} />
                      {supplyItems.length > 0 ? (
                        <div className="mt-2 space-y-1.5">{supplyItems.map(renderLine)}</div>
                      ) : (!its.length && quote.supply_detail ? <p className="mt-1 text-[12px] text-mute">{quote.supply_detail}</p> : null)}
                    </>
                  );
                })()}
                {quote.ttc_min != null ? (
                  <div className="mt-3">
                    {quote.vat_note ? <p className="mb-1 text-[11px] italic text-brand-on">{quote.vat_note}</p> : null}
                    <DRow label={t("r_ht")} value={`${eur(quote.total_min)} – ${eur(quote.total_max)} €`} />
                    <DRow label={t("r_vat")} value={`${eur(quote.vat_min)} – ${eur(quote.vat_max)} €`} />
                    <div className="mt-2 flex items-center justify-between border-t border-divider pt-3">
                      <span className="text-[15px] font-semibold text-ink">{t("r_ttc")}</span>
                      <span className="font-display text-lg font-bold text-brand">{eur(quote.ttc_min)} – {eur(quote.ttc_max)} €</span>
                    </div>
                  </div>
                ) : null}
              </Card>
            )}

            {quote.own_product_warning && (
              <div data-testid="own-warning" className="flex items-start gap-3 rounded-2xl bg-[#F7E9E6] p-4">
                <Icon name="alert-triangle" size={18} className="mt-0.5 shrink-0 text-err" />
                <p className="text-[13px] leading-relaxed text-err">{t("own_warn")}</p>
              </div>
            )}

            <div className="flex items-start gap-3 rounded-2xl bg-[#EAF0EB] p-4">
              <Icon name="check-circle" size={18} className="mt-0.5 shrink-0 text-ok" />
              <p className="text-[13px] leading-relaxed text-ink-3">{t("notice")}</p>
            </div>

            <div className="flex items-start gap-2 px-1">
              <Icon name="info" size={16} className="mt-0.5 shrink-0 text-mute" />
              <p className="text-[11px] leading-relaxed text-mute">{t("disclaimer")}</p>
            </div>
            <div className="flex items-start gap-2 px-1">
              <Icon name="file-text" size={16} className="mt-0.5 shrink-0 text-brand" />
              <p className="text-[11px] leading-relaxed text-brand">{t("fee_note")}</p>
            </div>

            <ContactActions />

            {quote.building_over_10y ? (
              <div data-testid="attest-notice" className="flex items-start gap-3 rounded-2xl bg-[#F5F1EC] p-4">
                <Icon name="file-check" size={18} className="mt-0.5 shrink-0 text-brand" />
                <p className="text-[13px] leading-relaxed text-brand-on">{t("attest_notice")}</p>
              </div>
            ) : null}

            <div>
              <p className="mb-2 text-[13px] font-medium text-ink-3">{t("download_label")}</p>
              <div className="flex flex-wrap gap-3">
                {getToken() ? (
                  <>
                    <GhostButton testId="export-pdf" title={t("btn_pdf")} icon="file-text" onClick={() => window.open(fileUrlAuth(`/quotes/${id}/pdf`), "_blank")} />
                    <GhostButton testId="export-word" title={t("btn_word")} icon="edit" onClick={() => window.open(fileUrlAuth(`/quotes/${id}/docx`), "_blank")} />
                  </>
                ) : null}
                {quote.building_over_10y ? (
                  <GhostButton testId="export-attestation" title={t("btn_attest")} icon="file-text" onClick={() => window.open(fileUrl(`/quotes/${id}/attestation`), "_blank")} />
                ) : null}
              </div>
              {!getToken() ? (
                <p data-testid="estimate-email-note" className="mt-2 text-[13px] leading-relaxed text-mute">{t("estimate_by_email_note")}</p>
              ) : null}
            </div>

            <div className="pt-2">
              <PrimaryButton testId="summary-done" title={t("done")} icon="home" onClick={() => navigate("/")} />
            </div>
          </div>
        )}
      </div>
      {pro ? (
        <footer className="border-t border-line px-5 py-5">
          <ProFooter pro={pro} />
        </footer>
      ) : null}
    </div>
  );
}

const detectCountry = (q) => {
  if (q?.client_country) return q.client_country;
  const p = (q?.client_phone || "").replace(/[\s.]/g, "");
  const map = { "+352": "LU", "+32": "BE", "+33": "FR", "+31": "NL", "+49": "DE", "+41": "CH", "+40": "RO" };
  for (const pre of Object.keys(map)) if (p.startsWith(pre)) return map[pre];
  return "BE";
};

const PartnersSection = ({ quote }) => {
  const { t } = useI18n();
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    const cat = quote?.category;
    if (!cat) return;
    // Mix papier peint / peinture : bannières des DEUX secteurs (papier peint + peinture).
    const cats = (cat === "wallpaper" && quote?.wp_mode === "mix") ? ["wallpaper", "painting"] : [cat];
    const country = detectCountry(quote);
    Promise.all(cats.map((c) => api.publicPartners(c, country).catch(() => [])))
      .then((lists) => {
        const seen = new Set(); const merged = [];
        lists.flat().forEach((p) => { if (p && p.id && !seen.has(p.id)) { seen.add(p.id); merged.push(p); } });
        setPartners(merged);
      });
  }, [quote?.category, quote?.wp_mode, quote?.client_country, quote?.client_phone]);
  if (!partners.length) return null;
  return (
    <div data-testid="partners-section" className="rounded-2xl border border-line bg-white p-5">
      <h3 className="mb-4 text-center text-[16px] font-semibold text-ink">{t("partners_title")}</h3>
      <div className="flex flex-wrap items-start justify-center gap-6">
        {partners.map((p) => (
          <div key={p.id} className="flex w-[140px] flex-col items-center text-center">
            <img data-testid={`partner-logo-${p.id}`} src={p.logo_url} alt={p.name} title={p.name}
              className="h-14 w-auto max-w-[140px] object-contain" />
            {p.tagline ? <span data-testid={`partner-tagline-${p.id}`} className="mt-1.5 text-[11px] leading-tight text-mute">{p.tagline}</span> : null}
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-[12px] leading-relaxed text-mute">{t("partners_note")}</p>
    </div>
  );
};


const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-line bg-white p-5">
    {title ? <h3 className="mb-3 text-[16px] font-semibold text-ink">{title}</h3> : null}
    {children}
  </div>
);

const DRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-divider py-2 last:border-0">
    <span className="text-[14px] text-mute">{label}</span>
    <span className="text-right text-[14px] font-medium text-ink">{value}</span>
  </div>
);
